// UserProfileDetails.jsx
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { ProfileHeader } from "./ProfileHeader";
import { PersonalInfoSection } from "./PersonalInfoSection";
import { SecuritySection } from "./SecuritySection";
import { BASE_URL} from './../../../../Api/Config'

export const UserProfileDetails = () => {
  const [userData, setUserData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        // API call to get profile
        const token = localStorage.getItem('token');
        const response = await fetch(`${BASE_URL}/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const profile = await response.json();
        setUserData(profile.user || profile);
        setError(null);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
        
        // Redirect to login if token is invalid
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (error && !userData.name) {
    return (
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              <p className="text-red-700">Failed to load profile: {error}</p>
            </div>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-2 bg-red-500 text-white px-4 py-2 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4">
      <div className="container mx-auto max-w-3xl space-y-8">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="text-center space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-redtxt">My Profile</h1>
            <p className="text-lightGray">Manage your account settings and preferences</p>
          </div>
        </motion.div>

        <ProfileHeader userData={userData} loading={loading} />
        <PersonalInfoSection 
          userData={userData} 
          onUpdateUser={setUserData} 
          isEditing={isEditing} 
          setIsEditing={setIsEditing}
          loading={loading}
        />
        <SecuritySection />
      </div>
    </section>
  );
};